// ════════════════════════════════════════════════════════════════════════════
// nexoSelfLearningEngine.js
// Institutional self-learning layer for Nexo.
// This sits ALONGSIDE nexoCommandBrain.js — it does not replace it.
// Responsibilities:
//   1. Correction capture: when the user corrects a misheard command, store it
//      as a direct transcript→intent mapping so it's never mis-heard again.
//   2. Confidence tracking: detect repeated recognition failures on the same
//      phrase and escalate to "please repeat slowly" instead of looping forever.
//   3. Command chaining: parse "do X then Y then Z" into a sequence Nexo can
//      execute one step at a time, waiting for each to settle.
//   4. Session context: remembers "the last thing I did" so "do the same for
//      banknifty" can substitute the target without re-stating the full command.
//   5. Anomaly / access-pattern detection: flags unusual usage so it can be
//      surfaced in the Command Audit tab (odd hours, repeated voice failures,
//      new/unrecognised face).
// ════════════════════════════════════════════════════════════════════════════

const STORAGE_KEY_CORRECTIONS = "nexo_self_learned_corrections_v1";
const STORAGE_KEY_FAILURES = "nexo_recognition_failures_v1";
const STORAGE_KEY_CONTEXT = "nexo_session_context_v1";
const STORAGE_KEY_ANOMALIES = "nexo_anomaly_log_v1";

const FAILURE_ESCALATE_THRESHOLD = 3;     // same phrase failing N times → escalate
const FAILURE_WINDOW_MS = 60 * 1000;       // within this window counts as "repeated"
const VOICE_FAIL_ANOMALY_THRESHOLD = 4;    // N voice mismatches in a row → anomaly
const ODD_HOUR_START = 0;                  // 12am
const ODD_HOUR_END = 5;                    // 5am — outside business hours flag

// ─── Generic storage helpers (localStorage-backed, swappable for backend) ────
function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable — fail silently, learning is best-effort
  }
}

function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^\w\s./',-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ═══════════════════════════════════════════════════════════════════════════
// 1. CORRECTION CAPTURE
// When Nexo executes the wrong thing and the user immediately corrects it
// ("no I meant start sensex", "wrong, click submit instead"), we store the
// ORIGINAL misheard phrase mapped directly to the CORRECTED command. Next
// time the same misheard phrase comes through, we skip straight to the
// corrected intent instead of re-running the fuzzy matchers.
// ═══════════════════════════════════════════════════════════════════════════

const CORRECTION_TRIGGER = /\b(no i meant|i meant|wrong|that's not right|thats not right|incorrect|not that|i said|correction|actually i meant)\b/;

export function isCorrectionUtterance(text) {
  return CORRECTION_TRIGGER.test(normalize(text));
}

// Strips the correction preamble to get the actual corrected command.
// "no I meant start sensex itm" → "start sensex itm"
export function extractCorrectedCommand(text) {
  return normalize(text)
    .replace(CORRECTION_TRIGGER, "")
    .replace(/^\s*(it'?s|its|i meant|i said)\s*/i, "")
    .trim();
}

export function recordCorrection(misheardText, correctedCommand) {
  const store = readJSON(STORAGE_KEY_CORRECTIONS, {});
  const key = normalize(misheardText);
  if (!key || !correctedCommand) return;
  store[key] = {
    correctedCommand: normalize(correctedCommand),
    hits: (store[key]?.hits || 0) + 1,
    lastSeen: Date.now(),
  };
  writeJSON(STORAGE_KEY_CORRECTIONS, store);
}

// Call this FIRST in your command pipeline — before fuzzy matching.
// If the current transcript closely matches a previously-corrected phrase,
// short-circuit straight to the known-good command.
export function lookupCorrection(text) {
  const store = readJSON(STORAGE_KEY_CORRECTIONS, {});
  const key = normalize(text);
  if (store[key]) return store[key].correctedCommand;

  // Fuzzy fallback — catch near-identical mis-hearings (small ASR variance)
  const keys = Object.keys(store);
  for (const k of keys) {
    if (k.length < 4) continue;
    const similarity = simpleSimilarity(key, k);
    if (similarity >= 0.85) return store[k].correctedCommand;
  }
  return "";
}

function simpleSimilarity(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  if (!longer.length) return 1;
  let matches = 0;
  for (let i = 0; i < shorter.length; i++) {
    if (longer.includes(shorter[i])) matches++;
  }
  return matches / longer.length;
}

// Stores last-N transcripts so when a correction utterance arrives, we know
// what it's correcting (the previous transcript, not the correction itself).
let recentTranscriptBuffer = [];

export function pushTranscriptHistory(text) {
  recentTranscriptBuffer = [...recentTranscriptBuffer.slice(-4), normalize(text)];
}

export function previousTranscript() {
  // second-to-last entry — the one BEFORE the correction utterance
  return recentTranscriptBuffer[recentTranscriptBuffer.length - 2] || "";
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. CONFIDENCE / FAILURE TRACKING
// If the same phrase fails to resolve N times within a window, stop retrying
// blindly — tell the user clearly and offer a typed fallback.
// ═══════════════════════════════════════════════════════════════════════════

export function recordRecognitionFailure(text) {
  const store = readJSON(STORAGE_KEY_FAILURES, []);
  const now = Date.now();
  const key = normalize(text);
  const fresh = store.filter(item => now - item.at < FAILURE_WINDOW_MS);
  fresh.push({ key, at: now });
  writeJSON(STORAGE_KEY_FAILURES, fresh.slice(-40));
  return fresh.filter(item => item.key === key).length;
}

export function shouldEscalateToManualEntry(text) {
  const store = readJSON(STORAGE_KEY_FAILURES, []);
  const now = Date.now();
  const key = normalize(text);
  const matchCount = store.filter(
    item => item.key === key && now - item.at < FAILURE_WINDOW_MS
  ).length;
  return matchCount >= FAILURE_ESCALATE_THRESHOLD;
}

export function clearFailureHistory() {
  writeJSON(STORAGE_KEY_FAILURES, []);
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. COMMAND CHAINING
// Parses multi-step utterances joined by "then" / "after that" / "and then"
// into an ordered array of sub-commands, executed sequentially with a pause
// between each so the page has time to navigate/render before the next step.
// ═══════════════════════════════════════════════════════════════════════════

const CHAIN_SEPARATORS = /\b(then|after that|and then|next|followed by)\b/gi;

export function isChainedCommand(text) {
  return CHAIN_SEPARATORS.test(text);
}

export function splitCommandChain(text) {
  return String(text || "")
    .split(CHAIN_SEPARATORS)
    .map(part => part.trim())
    .filter(part => part && part.length > 1 && !/^(then|after that|and then|next|followed by)$/i.test(part));
}

// Runs each sub-command through the provided executor function in sequence,
// waiting `stepDelayMs` between steps so navigation/render settles.
export async function runCommandChain(steps, executor, stepDelayMs = 900) {
  const results = [];
  for (const step of steps) {
    try {
      const outcome = await executor(step);
      results.push({ step, outcome, ok: true });
    } catch (err) {
      results.push({ step, error: err?.message || String(err), ok: false });
    }
    await new Promise(resolve => setTimeout(resolve, stepDelayMs));
  }
  return results;
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. SESSION CONTEXT — "do the same for X"
// Remembers the last executed command's structure (action + target type) so
// a follow-up like "now do the same for banknifty" or "same thing on sensex"
// can substitute just the target.
// ═══════════════════════════════════════════════════════════════════════════

const REPEAT_PATTERN = /\b(do the same|same thing|same for|repeat that for|now do|also do)\b/;

export function isRepeatWithSubstitution(text) {
  return REPEAT_PATTERN.test(normalize(text));
}

export function recordLastCommand(action, targetPhrase, fullText) {
  writeJSON(STORAGE_KEY_CONTEXT, {
    action,
    targetPhrase: normalize(targetPhrase || ""),
    fullText: normalize(fullText || ""),
    at: Date.now(),
  });
}

export function getLastCommand() {
  const ctx = readJSON(STORAGE_KEY_CONTEXT, null);
  if (!ctx) return null;
  // Context expires after 10 minutes of inactivity — avoids stale substitutions
  if (Date.now() - ctx.at > 10 * 60 * 1000) return null;
  return ctx;
}

// Builds the substituted command text.
// e.g. lastCommand = {action:"start", targetPhrase:"sensex itm"}, newTarget="banknifty"
// → "start banknifty"
export function buildSubstitutedCommand(text) {
  const last = getLastCommand();
  if (!last) return "";

  // Extract whatever comes after "for" / "on" / "with" in the new utterance
  const newTargetMatch = normalize(text).match(/\b(?:for|on|with|to)\s+([a-z0-9 ]+)$/);
  const newTarget = newTargetMatch?.[1]?.trim();
  if (!newTarget) return "";

  return `${last.action} ${newTarget}`.trim();
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. ANOMALY / ACCESS-PATTERN DETECTION
// Institutional desks need to know if Nexo is being used unexpectedly:
//  - odd-hour activation
//  - repeated voice tone mismatches (possible impersonation attempt)
//  - face scan score suddenly dropping (different person / spoofing attempt)
// These get logged locally and can be pushed to the backend audit log.
// ═══════════════════════════════════════════════════════════════════════════

export function checkOddHourActivation() {
  const hour = new Date().getHours();
  return hour >= ODD_HOUR_START && hour <= ODD_HOUR_END;
}

let voiceFailStreak = 0;

export function recordVoiceVerificationResult(passed) {
  voiceFailStreak = passed ? 0 : voiceFailStreak + 1;
  if (voiceFailStreak >= VOICE_FAIL_ANOMALY_THRESHOLD) {
    logAnomaly("repeated_voice_mismatch", `${voiceFailStreak} consecutive voice verification failures`);
    voiceFailStreak = 0; // reset after flagging so we don't spam
    return true;
  }
  return false;
}

export function logAnomaly(type, detail) {
  const store = readJSON(STORAGE_KEY_ANOMALIES, []);
  store.push({
    type,
    detail,
    at: new Date().toISOString(),
  });
  writeJSON(STORAGE_KEY_ANOMALIES, store.slice(-200));
}

export function getAnomalyLog() {
  return readJSON(STORAGE_KEY_ANOMALIES, []);
}

export function clearAnomalyLog() {
  writeJSON(STORAGE_KEY_ANOMALIES, []);
}

// Call once when the agent starts — flags odd-hour activations automatically.
export function checkStartupAnomalies() {
  if (checkOddHourActivation()) {
    logAnomaly("odd_hour_start", `Nexo started at ${new Date().toLocaleTimeString()}`);
    return true;
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. AUTO-VOCABULARY EXPANSION
// Every time a command succeeds, the words used get added to a frequency
// table. High-frequency unrecognised words become candidates Nexo suggests
// to the admin for promotion into the command brain (via the Training tab).
// ═══════════════════════════════════════════════════════════════════════════

const STORAGE_KEY_VOCAB = "nexo_vocab_frequency_v1";
const STOP_WORDS = new Set([
  "the","a","an","to","on","in","for","of","and","is","this","that","my","please",
  "can","you","could","would","i","want","need","just","now","go","ahead","boss","nexo","hey",
]);

export function trackSuccessfulVocabulary(text) {
  const store = readJSON(STORAGE_KEY_VOCAB, {});
  normalize(text)
    .split(" ")
    .filter(word => word.length > 2 && !STOP_WORDS.has(word))
    .forEach(word => {
      store[word] = (store[word] || 0) + 1;
    });
  writeJSON(STORAGE_KEY_VOCAB, store);
}

export function getTopUnrecognisedCandidates(knownTerms = [], limit = 15) {
  const store = readJSON(STORAGE_KEY_VOCAB, {});
  const known = new Set(knownTerms.map(normalize));
  return Object.entries(store)
    .filter(([word]) => !known.has(word))
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([word, count]) => ({ word, count }));
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT BUNDLE — convenience object for single-import usage
// ═══════════════════════════════════════════════════════════════════════════

export default {
  // corrections
  isCorrectionUtterance,
  extractCorrectedCommand,
  recordCorrection,
  lookupCorrection,
  pushTranscriptHistory,
  previousTranscript,
  // failure tracking
  recordRecognitionFailure,
  shouldEscalateToManualEntry,
  clearFailureHistory,
  // chaining
  isChainedCommand,
  splitCommandChain,
  runCommandChain,
  // session context
  isRepeatWithSubstitution,
  recordLastCommand,
  getLastCommand,
  buildSubstitutedCommand,
  // anomaly detection
  checkOddHourActivation,
  recordVoiceVerificationResult,
  logAnomaly,
  getAnomalyLog,
  clearAnomalyLog,
  checkStartupAnomalies,
  // vocabulary
  trackSuccessfulVocabulary,
  getTopUnrecognisedCandidates,
};